import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ShieldCheck, ShieldAlert, RefreshCw, CheckCircle, AlertTriangle,
  Clock, Sparkles, User, FileText, Play, Upload, Send, Download, X, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KycTabView = ({ caseId }) => {
  const { t } = useTranslation('kyc');
  const [kycStatus, setKycStatus] = useState(null);
  const [allRuns, setAllRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screening, setScreening] = useState(false);
  const [activating, setActivating] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendingForm, setSendingForm] = useState(false);
  const [showSendFormModal, setShowSendFormModal] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const fileInputRef = useRef(null);

  const fetchKycData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      const statusRes = await axios.get(`${API}/kyc/case/${caseId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKycStatus(statusRes.data);
      
      const runsRes = await axios.get(`${API}/kyc/case/${caseId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setAllRuns(runsRes.data || []);
      
      const docsRes = await axios.get(`${API}/kyc/case/${caseId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setDocuments(docsRes.data || []);
      
    } catch (error) {
      console.error('Failed to fetch KYC data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKycData();
  }, [caseId]);

  const handleActivateKyc = async () => {
    setActivating(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      await axios.post(`${API}/kyc/case/${caseId}/activate`, {
        trigger_reason: 'MANUAL_ACTIVATION',
        transaction_type: 'GENERAL'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('status.notActivated') + ' ✓');
      await fetchKycData();
    } catch (error) {
      toast.error('Error');
    } finally {
      setActivating(false);
    }
  };

  const handleRunScreening = async () => {
    setScreening(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      await axios.post(`${API}/kyc/case/${caseId}/screen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Screening ✓');
      await fetchKycData();
    } catch (error) {
      toast.error('Screening failed');
    } finally {
      setScreening(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', 'ID_DOCUMENT');
        formData.append('uploaded_by', 'LAWYER');

        await axios.post(`${API}/kyc/case/${caseId}/upload-document`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('Upload ✓');
      await fetchKycData();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSendKycFormToClient = async () => {
    if (!clientEmail || !clientEmail.includes('@')) {
      toast.error('Invalid email');
      return;
    }

    setSendingForm(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      await axios.post(`${API}/kyc/case/${caseId}/send-form`, {
        client_email: clientEmail,
        form_type: 'KYC_QUESTIONNAIRE'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`KYC → ${clientEmail} ✓`);
      setShowSendFormModal(false);
      setClientEmail('');
      await fetchKycData();
    } catch (error) {
      toast.error('Send failed');
    } finally {
      setSendingForm(false);
    }
  };

  const getRiskConfig = (level) => {
    const configs = {
      LOW: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: t('risk.low') },
      MEDIUM: { bg: 'bg-amber-50', text: 'text-amber-600', label: t('risk.medium') },
      HIGH: { bg: 'bg-red-50', text: 'text-red-600', label: t('risk.high') }
    };
    return configs[level] || configs.LOW;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-300" />
      </div>
    );
  }

  // KYC not activated state
  if (!kycStatus?.requires_kyc) {
    return (
      <div className="max-w-lg mx-auto py-16 px-8">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-5 rounded-full bg-gray-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-gray-400" />
          </div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            {t('status.notActivated')}
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            {t('status.notActivatedDesc')}
          </p>
          <button
            onClick={handleActivateKyc}
            disabled={activating}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {activating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {activating ? t('actions.activating') : t('actions.activateKyc')}
          </button>
        </div>
      </div>
    );
  }

  const latestRun = kycStatus?.latest_run;
  const status = kycStatus?.kyc_status;
  const isCleared = status === 'SCREENED_CLEAR' || status === 'APPROVED';
  const hasHits = status === 'SCREENED_HIT';

  return (
    <>
      <div className="max-w-3xl mx-auto py-8 px-6 space-y-8">
        
        {/* Status Card */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isCleared ? 'bg-emerald-50' : hasHits ? 'bg-amber-50' : 'bg-gray-100'
                }`}>
                  {isCleared ? (
                    <ShieldCheck className="w-5 h-5 text-emerald-500" />
                  ) : hasHits ? (
                    <ShieldAlert className="w-5 h-5 text-amber-500" />
                  ) : (
                    <Shield className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-base font-medium text-gray-900">
                    {isCleared ? t('status.noHits') : hasHits ? t('status.hitsFound') : t('status.waitingScreening')}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {isCleared 
                      ? t('status.noHitsDesc')
                      : hasHits 
                      ? t('status.hitsFoundDesc')
                      : t('status.waitingScreeningDesc')
                    }
                  </p>
                </div>
              </div>
              
              {status !== 'APPROVED' && status !== 'REJECTED' && (
                <button
                  onClick={handleRunScreening}
                  disabled={screening}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {screening ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  {screening ? t('actions.running') : t('actions.runScreening')}
                </button>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 border-t border-gray-100">
            <div className="p-4 text-center border-r border-gray-100">
              <div className="text-xs text-gray-400 mb-1">{t('stats.runs')}</div>
              <div className="text-lg font-semibold text-gray-900">{allRuns.length}</div>
            </div>
            <div className="p-4 text-center border-r border-gray-100">
              <div className="text-xs text-gray-400 mb-1">{t('stats.pep')}</div>
              <div className={`text-lg font-semibold ${latestRun?.pep_matches > 0 ? 'text-amber-500' : 'text-gray-900'}`}>
                {latestRun?.pep_matches || 0}
              </div>
            </div>
            <div className="p-4 text-center border-r border-gray-100">
              <div className="text-xs text-gray-400 mb-1">{t('stats.sanctions')}</div>
              <div className={`text-lg font-semibold ${latestRun?.sanctions_matches > 0 ? 'text-red-500' : 'text-gray-900'}`}>
                {latestRun?.sanctions_matches || 0}
              </div>
            </div>
            <div className="p-4 text-center">
              <div className="text-xs text-gray-400 mb-1">{t('stats.risk')}</div>
              {kycStatus?.kyc_risk_level ? (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRiskConfig(kycStatus.kyc_risk_level).bg} ${getRiskConfig(kycStatus.kyc_risk_level).text}`}>
                  {getRiskConfig(kycStatus.kyc_risk_level).label}
                </span>
              ) : (
                <span className="text-sm text-gray-300">—</span>
              )}
            </div>
          </div>
        </div>

        {/* AI Analysis */}
        {latestRun?.ai_summary && (
          <div className="bg-gray-50 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('aiAnalysis.title')}</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {latestRun.ai_summary}
            </p>
            {latestRun.ai_recommended_risk_level && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <span className="text-xs text-gray-400">{t('aiAnalysis.recommendedRisk')}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRiskConfig(latestRun.ai_recommended_risk_level).bg} ${getRiskConfig(latestRun.ai_recommended_risk_level).text}`}>
                  {getRiskConfig(latestRun.ai_recommended_risk_level).label}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">{t('documents.title')}</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSendFormModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                {t('documents.sendForm')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" />
                {uploading ? t('documents.uploading') : t('documents.upload')}
              </button>
            </div>
          </div>

          {documents.length > 0 ? (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      doc.verification_status === 'VERIFIED' ? 'bg-emerald-50' : 'bg-gray-100'
                    }`}>
                      <FileText className={`w-4 h-4 ${
                        doc.verification_status === 'VERIFIED' ? 'text-emerald-500' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {doc.original_filename || 'Document'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.verification_status === 'VERIFIED' && (
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    )}
                    <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">{t('documents.none')}</p>
            </div>
          )}
        </div>

        {/* KYC Profiles */}
        {kycStatus?.profiles?.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">{t('profiles.title')}</h3>
            <div className="space-y-2">
              {kycStatus.profiles.map((profile) => (
                <div
                  key={profile.profile_id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{profile.full_name}</div>
                      <div className="text-xs text-gray-400">
                        {profile.profile_type === 'PERSON' ? t('profiles.person') : t('profiles.company')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {profile.pep_status === 'HIT' && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-600">PEP</span>
                    )}
                    {profile.sanctions_status === 'HIT' && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600">Sanction</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      profile.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-600' :
                      profile.status === 'REJECTED' ? 'bg-red-50 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {profile.status === 'APPROVED' ? t('profiles.approved') : 
                       profile.status === 'REJECTED' ? t('profiles.rejected') : t('profiles.pending')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Screening History */}
        {allRuns.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">{t('history.title')}</h3>
            <div className="space-y-2">
              {allRuns.slice(0, 5).map((run, idx) => (
                <div
                  key={run.run_id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      run.total_matches > 0 ? 'bg-amber-50' : 'bg-emerald-50'
                    }`}>
                      {run.total_matches > 0 ? (
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                      ) : (
                        <ShieldCheck className="w-4 h-4 text-emerald-500" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {t('history.screening')} #{allRuns.length - idx}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(run.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <span className={`text-sm ${run.total_matches > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                    {run.total_matches} {t('history.hits')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Send Form Modal */}
      <AnimatePresence>
        {showSendFormModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-gray-900">{t('sendModal.title')}</h3>
                <button
                  onClick={() => setShowSendFormModal(false)}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-500">
                  <strong className="text-gray-700">{t('sendModal.whatHappens')}</strong>
                </p>
                <ul className="text-xs text-gray-500 mt-1 space-y-1">
                  <li>• {t('sendModal.step1')}</li>
                  <li>• {t('sendModal.step2')}</li>
                  <li>• {t('sendModal.step3')}</li>
                </ul>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('sendModal.emailLabel')}
                </label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder={t('sendModal.emailPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowSendFormModal(false)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  {t('sendModal.cancel')}
                </button>
                <button
                  onClick={handleSendKycFormToClient}
                  disabled={sendingForm || !clientEmail}
                  className="flex-1 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingForm ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {sendingForm ? t('sendModal.sending') : t('sendModal.send')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default KycTabView;
